import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, TrendingUp, Cpu, Filter, Zap, AlertCircle } from "lucide-react";

const MEEnginePerformance = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const powerCurvePhases = [
    {
      id: "data-validation",
      title: "Data Validation Phase",
      description: "Filter valid performance records for the target engine",
      formula: "Record must have: mainEngine, averageRPM > 0, mekwhCounter > 0, and match engineId",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "mcr-calculation",
      title: "MCR Determination",
      description: "Calculate Maximum Continuous Rating from valid records",
      formula: "MCR RPM = max(averageRPM), MCR Power = max(mekwhCounter)",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "curve-generation-methods",
      title: "Curve Generation Methods",
      description: "Different algorithms based on data availability",
      formula: "≥5 records: Actual data with tolerance. <5 records: Hybrid interpolation",
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "rpm-tolerance-matching",
      title: "RPM Tolerance Matching",
      description: "Find records within ±2.5% of target RPM for actual data method",
      formula: "Tolerance = MCR RPM × 0.025, Find records within RPM ± tolerance",
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    },
    {
      id: "interpolation-logic",
      title: "Hybrid Interpolation Logic",
      description: "Linear interpolation between actual points, cubic extrapolation beyond",
      formula: "Power % = lower + (upper-lower) × (target-lower_rpm) ÷ (upper_rpm-lower_rpm)",
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "overload-curve",
      title: "Overload Curve Calculation",
      description: "110% of engine curve up to 100% RPM, then decreasing overload",
      formula: "≤100% RPM: min(basePower × 1.10, 110). >100% RPM: overloadFactor = 1.1 - (percent-100)×0.02",
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
  ];

  const calculations = [
    {
      id: "torque-calculation",
      title: "Torque Calculation",
      description: "Engine torque derived from power and RPM using physics formula",
      formula: "Torque (kNm) = (60 × Power (kW)) ÷ (2π × RPM)",
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "cubic-estimation",
      title: "Cubic Power Estimation",
      description: "Fallback estimation when no matching records found",
      formula: "Power % = (RPM % ÷ 100)³ × 100",
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
    {
      id: "data-point-validation",
      title: "Data Point Requirements",
      description: "Minimum requirements for dynamic power curve generation",
      formula: "Need ≥1 valid record with: engine match, RPM > 0, power > 0",
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#c5fcdd] rounded-lg">
              <Calculator className="w-6 h-6 text-[#27ae60]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Engine Performance
              </h2>
              <p className="text-gray-600 text-sm">
                Real-time engine performance curve calculation from vessel data
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Power Curve Generation Phases */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Power Curve Generation Phases</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {powerCurvePhases.map((phase) => (
                  <div
                    key={phase.id}
                    className={`p-5 rounded-lg border ${phase.borderColor} ${phase.bgColor}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-white border">
                        {phase.id === "data-validation" && <Filter className="w-5 h-5 text-blue-600" />}
                        {phase.id === "mcr-calculation" && <TrendingUp className="w-5 h-5 text-green-600" />}
                        {phase.id === "curve-generation-methods" && <Cpu className="w-5 h-5 text-purple-600" />}
                        {phase.id === "rpm-tolerance-matching" && <AlertCircle className="w-5 h-5 text-orange-600" />}
                        {phase.id === "interpolation-logic" && <Calculator className="w-5 h-5 text-red-600" />}
                        {phase.id === "overload-curve" && <Zap className="w-5 h-5 text-amber-600" />}
                      </div>
                      <h4 className="font-semibold text-gray-800">
                        {phase.title}
                      </h4>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                      <p className="text-sm text-gray-600">{phase.description}</p>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                      <div className="text-sm font-mono bg-white p-2 rounded border">
                        {phase.formula}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Calculations */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Supporting Calculations</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <h4 className="font-semibold text-gray-800 mb-3">
                      {calc.title}
                    </h4>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                      <p className="text-sm text-gray-600">{calc.description}</p>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                      <div className="text-sm font-mono bg-white p-2 rounded border">
                        {calc.formula}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MEEnginePerformance;